<template>
  <div class="space-y-4">
    <slot name="header" />

    <div
      v-for="(item, index) in pagedItems[currentPage]"
      :key="item.id"
      @click="select(item.id)"
      class="flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer transition hover:bg-base-200"
      :class="{ 'ring-2 ring-primary ring-offset-2': selectedId === item.id }">
      <slot name="item" :item="item" />
    </div>

    <div class="flex justify-between mt-4">
      <button class="btn btn-sm" :disabled="currentPage === 0" @click="prevPage">
        ← {{ t("ui.inputs.pagination.previous") }}
      </button>
      <button class="btn btn-sm" :disabled="currentPage === totalPages - 1" @click="nextPage">
        {{ t("ui.inputs.pagination.next") }} →
      </button>
    </div>

    <div v-if="showSelectionPreview && selectedItem" class="mt-6 p-4 border rounded bg-base-100">
      <slot name="preview" :item="selectedItem" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { SelectableItemDto } from "@/features/blueprint/dtos/SelectableItemDto";
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

export type SelectableItem = SelectableItemDto;

const props = defineProps<{
  items: SelectableItemDto[];
  perPage?: number;
  showSelectionPreview?: boolean;
  modelValue?: string | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | null): void;
}>();

const perPage = props.perPage ?? 3;
const currentPage = ref(0);
const selectedId = ref(props.modelValue ?? null);

watch(
  () => props.modelValue,
  val => {
    selectedId.value = val ?? null;
  }
);

function select(id: string) {
  selectedId.value = id;
  emit("update:modelValue", id);
}

function prevPage() {
  currentPage.value = Math.max(currentPage.value - 1, 0);
}

function nextPage() {
  currentPage.value = Math.min(currentPage.value + 1, totalPages.value - 1);
}

const pagedItems = computed(() => {
  const pages = [];
  for (let i = 0; i < props.items.length; i += perPage) {
    pages.push(props.items.slice(i, i + perPage));
  }
  return pages;
});

const totalPages = computed(() => pagedItems.value.length);
const selectedItem = computed(() => props.items.find(i => i.id === selectedId.value) ?? null);
</script>
